import { h, type View } from './dom.js';
import { ApiError, RequestAbortError, type ApiClient } from '../../api/client.js';
import { createDeviceControlsApi, type ActionType, type DeviceAction, type DeviceActionRequest, type DeviceControl, type DeviceReport } from '../../api/device-controls.js';
import { decodeCameraJpeg } from '../../device-usb/codec.js';

interface CreateOptions { api: ApiClient; deviceId: string; onControl?: (control: DeviceControl) => void; }

const PREVIEW_MAX_AGE_MS = 60_000;
const POLL_MIN_MS = 1000;
const POLL_MAX_MS = 1500;
const POLL_DEADLINE_MS = 65_000;

function isCameraType(type: ActionType): boolean { return type === 'camera.capture'; }

export function reportLabel(report: DeviceReport | null): string {
  if (!report) return 'Estado todavía no disponible.';
  if (report.speaking) return 'Reproduciendo respuesta.';
  if (report.capturing) return 'Captando conversación.';
  if (report.wakeEnabled) return 'Detección local «Oye ASIS» activa.';
  if (report.audioEnabled) return 'Audio habilitado en el dispositivo.';
  return 'Audio deshabilitado.';
}

function describeFailure(action: DeviceAction): string {
  if (action.state === 'expired') return 'La acción caducó sin confirmación del dispositivo.';
  return 'El dispositivo rechazó la acción. Comprueba su estado y vuelve a intentarlo.';
}

function actionText(action: DeviceAction | null, control: DeviceControl | null, operationText: string | null): string {
  if (action) {
    if (action.state === 'queued' || action.state === 'running') return 'Pendiente del dispositivo.';
    if (action.state === 'succeeded') return 'Acción confirmada por el dispositivo.';
    if (action.state === 'expired') return 'La acción caducó sin confirmación del dispositivo.';
    return 'El dispositivo rechazó la acción.';
  }
  if (control && !control.supported) return 'El dispositivo aún no anuncia controles por Wi-Fi.';
  if (operationText) return operationText;
  if (control?.report) return `Último estado: ${reportLabel(control.report)}`;
  return 'Cargando estado del dispositivo…';
}

function classifyError(error: unknown): string {
  if (error instanceof RequestAbortError) return 'Operación cancelada.';
  if (error instanceof ApiError) {
    if (error.status === 401) return 'Tu sesión ha caducado. Entra de nuevo en ASIS.';
    if (error.status === 403) return 'La cuenta propietaria no autoriza esta acción.';
    if (error.status === 404) return 'El dispositivo no está disponible.';
    if (error.status === 429) return 'Espera un momento antes de volver a intentarlo.';
    if (error.status >= 500) return 'El servidor no pudo completar la acción. Inténtalo otra vez.';
    return 'No se pudo completar la acción.';
  }
  return 'No se pudo completar la acción.';
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise(resolve => {
    if (signal.aborted) { resolve(); return; }
    const done = () => { clearTimeout(timer); signal.removeEventListener('abort', done); resolve(); };
    const timer = setTimeout(done, ms);
    signal.addEventListener('abort', done, { once: true });
  });
}

function jitter(min: number, max: number): number { return Math.round(min + Math.random() * (max - min)); }

export function createDeviceControls({ api, deviceId, onControl }: CreateOptions): View {
  const controls = createDeviceControlsApi(api);
  let alive = true;
  const lifetime = new AbortController();
  let sending = false;
  let loadToken = 0;
  let actionToken = 0;
  let currentControl: DeviceControl | null = null;
  let currentAction: DeviceAction | null = null;
  let operationText: string | null = null;
  let operationResult: string | null = null;
  let pollAbort: AbortController | null = null;
  let previewUrl: string | null = null;
  let previewTimer: ReturnType<typeof setTimeout> | undefined;

  const root = h('section', { class: 'card device-controls', 'aria-label': 'Control por Wi-Fi' });
  const sub = h('p', { class: 'card-sub' }, ['Los cambios se aplican al volver al reposo. Para parar al momento, usa el botón del aparato.']);
  const status = h('p', { class: 'card-sub', role: 'status', 'aria-live': 'polite', 'aria-atomic': 'true' });
  const error = h('p', { class: 'auth-error', role: 'alert', hidden: true });
  const form = h('div', { class: 'form-stack' });
  const actionInfo = h('p', { class: 'card-sub' });

  const audioConsent = h('input', { type: 'checkbox' });
  const audioConsentLabel = h('label', { class: 'field inline' }, [
    audioConsent,
    ' He comprobado el cableado del micrófono y altavoz.',
  ]);
  const wakeConsent = h('input', { type: 'checkbox' });
  const wakeConsentLabel = h('label', { class: 'field inline' }, [
    wakeConsent,
    ' Permitir que esta unidad escuche «Oye ASIS».',
  ]);
  const wakeExplain = h('p', { class: 'card-sub' }, ['Abre una conversación al llamarlo.']);
  const cameraConsent = h('input', { type: 'checkbox' });
  const cameraConsentLabel = h('label', { class: 'field inline' }, [
    cameraConsent,
    ' Tomar una foto solo en esta vista. Se guarda temporalmente en la memoria del servidor y nunca se envía a Gemini.',
  ]);

  const audioEnable = h('button', { class: 'btn primary', type: 'button' }, ['Habilitar audio']);
  const audioDisable = h('button', { class: 'btn ghost', type: 'button' }, ['Silenciar ASIS']);
  const wakeEnable = h('button', { class: 'btn primary', type: 'button' }, ['Activar Oye ASIS']);
  const wakeDisable = h('button', { class: 'btn ghost', type: 'button' }, ['Desactivar activación']);
  const sessionStop = h('button', { class: 'btn ghost', type: 'button' }, ['Parar conversación']);
  const cameraCapture = h('button', { class: 'btn primary', type: 'button' }, ['Tomar foto']);
  const refreshBtn = h('button', { class: 'btn ghost', type: 'button' }, ['Actualizar estado']);

  const previewHost = h('div', { class: 'usb-camera-preview', hidden: true });
  const previewImg = h('img', { alt: 'Vista previa de la foto capturada por el dispositivo' });
  const previewStatus = h('p', { class: 'card-sub' });
  previewHost.append(previewImg, previewStatus);

  const audioRow = h('div', { class: 'form-row' }, [audioEnable, audioDisable]);
  const wakeRow = h('div', { class: 'form-row' }, [wakeEnable, wakeDisable]);
  const sessionRow = h('div', { class: 'form-row' }, [sessionStop, cameraCapture, refreshBtn]);

  const audioState = h('span', { class: 'tag' });
  const wakeState = h('span', { class: 'tag' });
  const audioGroup = h('section', { class: 'device-control-group' }, [h('div', { class: 'today-panel-head' }, [h('h4', {}, ['Audio']), audioState]), audioConsentLabel, audioRow]);
  const wakeGroup = h('section', { class: 'device-control-group' }, [h('div', { class: 'today-panel-head' }, [h('h4', {}, ['«Oye ASIS»']), wakeState]), wakeExplain, wakeConsentLabel, wakeRow]);
  const extra = h('details', { class: 'secondary-details' }, [h('summary', {}, ['Cámara de la unidad']), cameraConsentLabel, cameraCapture, previewHost]);
  sessionRow.replaceChildren(sessionStop, refreshBtn);
  form.append(audioGroup, wakeGroup, sessionRow, extra, actionInfo);
  extra.append(sub);
  root.append(status, error, form);

  for (const [button, label] of [[audioEnable, 'Audio'], [audioDisable, 'Audio'], [wakeEnable, 'Oye ASIS'], [wakeDisable, 'Oye ASIS']] as const) {
    const text = button.textContent ?? '';
    button.replaceChildren(h('span', { class: 'visually-hidden' }, [text]));
    button.className = 'btn control-switch'; button.setAttribute('role', 'switch');
    button.setAttribute('aria-label', `${label}: ${text}`);
  }

  function releasePreview(): void {
    if (previewTimer) { clearTimeout(previewTimer); previewTimer = undefined; }
    if (previewUrl) { URL.revokeObjectURL(previewUrl); previewUrl = null; }
    previewImg.removeAttribute('src');
    previewHost.hidden = true;
    previewStatus.textContent = '';
  }

  function isPending(): boolean {
    if (sending) return true;
    if (!currentAction) return false;
    return currentAction.state === 'queued' || currentAction.state === 'running';
  }

  function setError(message: string | null): void {
    if (!message) { error.textContent = ''; error.hidden = true; return; }
    error.textContent = message;
    error.hidden = false;
  }

  function refreshStatus(): void {
    status.textContent = actionText(currentAction, currentControl, operationText);
    if (operationResult) {
      actionInfo.textContent = operationResult;
    } else if (currentAction && (currentAction.state === 'queued' || currentAction.state === 'running')) {
      actionInfo.textContent = 'Esperando confirmación del dispositivo…';
    } else {
      actionInfo.textContent = '';
    }
  }

  function updateControls(): void {
    const report = currentControl?.report ?? null;
    const audioCap = report?.capabilities.audio ?? false;
    const wakeCap = report?.capabilities.wake ?? false;
    const cameraCap = report?.capabilities.camera ?? false;
    const audioEnabled = report?.audioEnabled ?? false;
    const wakeEnabled = report?.wakeEnabled ?? false;
    const supported = currentControl?.supported ?? false;

    audioConsent.disabled = !supported;
    wakeConsent.disabled = !supported || !audioEnabled || !wakeCap;
    cameraConsent.disabled = !supported || !cameraCap;

    audioState.textContent = !report ? 'Sin confirmar' : !audioCap ? 'No disponible' : audioEnabled ? 'Activado' : 'Desactivado';
    wakeState.textContent = !report ? 'Sin confirmar' : !wakeCap ? 'No disponible' : wakeEnabled ? 'Activado' : 'Desactivado';
    const pending = isPending();
    const allowAudioOps = supported && audioCap && !pending;
    const allowWakeOps = supported && audioCap && wakeCap && audioEnabled && !pending;
    const allowCamera = supported && cameraCap && !pending;
    const allowSession = supported && audioCap;

    audioEnable.disabled = !allowAudioOps || !audioConsent.checked;
    audioDisable.disabled = !supported || !audioCap || (!audioEnabled && !pending);
    wakeEnable.disabled = !allowWakeOps || !wakeConsent.checked;
    wakeDisable.disabled = !supported || !wakeCap || (!wakeEnabled && !pending);
    audioEnable.hidden = audioEnabled || pending; audioDisable.hidden = !audioEnabled && !pending;
    wakeEnable.hidden = wakeEnabled || pending; wakeDisable.hidden = !wakeEnabled && !pending;
    for (const button of [audioEnable, audioDisable]) button.setAttribute('aria-checked', String(audioEnabled));
    for (const button of [wakeEnable, wakeDisable]) button.setAttribute('aria-checked', String(wakeEnabled));
    audioConsentLabel.hidden = audioEnabled || !supported || !audioCap;
    wakeConsentLabel.hidden = wakeEnabled || !supported || !wakeCap;
    sessionStop.hidden = !report?.speaking && !report?.capturing && !pending;
    form.setAttribute('aria-busy', String(pending));
    for (const [enable, disable] of [[audioEnable, audioDisable], [wakeEnable, wakeDisable]]) {
      if (document.activeElement === enable && enable!.hidden) disable!.focus();
      else if (document.activeElement === disable && disable!.hidden) enable!.focus();
    }
    sessionStop.disabled = !allowSession;
    cameraCapture.disabled = !allowCamera || !cameraConsent.checked;
    refreshBtn.disabled = false;
  }

  async function refreshControlAfterTerminal(): Promise<void> {
    if (!alive) return;
    const id = ++loadToken;
    try {
      const control = await controls.getControl(deviceId, lifetime.signal);
      if (!alive || id !== loadToken) return;
      currentControl = control;
      onControl?.(control);
      refreshStatus();
      updateControls();
    } catch { /* keep prior status text; status will refresh on next user request */ }
  }

  async function loadControl(): Promise<void> {
    const id = ++loadToken;
    try {
      const control = await controls.getControl(deviceId, lifetime.signal);
      if (!alive || id !== loadToken || lifetime.signal.aborted) return;
      currentControl = control;
      onControl?.(control);
      setError(null);
      if (!isPending() && control.activeAction && ['queued', 'running'].includes(control.activeAction.state)) {
        currentAction = control.activeAction;
        const token = ++actionToken;
        stopPolling(); pollAbort = new AbortController();
        void pollAction(currentAction.id, Date.now(), token);
      }
      refreshStatus();
      updateControls();
    } catch (err) {
      if (!alive || id !== loadToken) return;
      if (err instanceof RequestAbortError) return;
      setError(classifyError(err));
    }
  }

  async function loadCameraImage(actionId: string, token: number): Promise<void> {
    try {
      const data = await controls.image(deviceId, actionId, lifetime.signal);
      if (!alive || token !== actionToken) return;
      if (data.mimeType !== 'image/jpeg') throw new Error('invalid_image');
      let bytes: Uint8Array<ArrayBuffer>;
      try { bytes = decodeCameraJpeg(data.imageBase64); }
      finally { data.imageBase64 = ''; }
      const blob = new Blob([bytes], { type: 'image/jpeg' });
      bytes.fill(0);
      const url = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      previewUrl = url;
      previewImg.src = url;
      previewHost.hidden = false;
      previewStatus.textContent = 'Foto temporal. Se elimina de esta vista en un minuto o al salir.';
      if (previewTimer) clearTimeout(previewTimer);
      previewTimer = setTimeout(releasePreview, PREVIEW_MAX_AGE_MS);
    } catch (err) {
      if (!alive || token !== actionToken) return;
      if (err instanceof RequestAbortError) return;
      releasePreview();
      setError('La foto no está disponible o no es válida. No se tomará otra automáticamente.');
    }
  }

  function stopPolling(): void {
    if (pollAbort) { pollAbort.abort(); pollAbort = null; }
  }

  async function pollAction(actionId: string, startedAt: number, token: number): Promise<void> {
    while (alive && token === actionToken) {
      const remaining = POLL_DEADLINE_MS - (Date.now() - startedAt);
      if (remaining <= 0) {
        if (!alive || token !== actionToken) return;
        // A local timeout is not a device receipt.
        currentAction = null;
        operationText = null;
        operationResult = 'No llegó confirmación a tiempo. Actualiza el estado; la acción no se reenvía automáticamente.';
        refreshStatus();
        updateControls();
        void refreshControlAfterTerminal();
        return;
      }
      const ctl = pollAbort!;
      try {
        const { action } = await controls.get(deviceId, actionId, AbortSignal.any([ctl.signal, lifetime.signal]));
        if (!alive || token !== actionToken) return;
        currentAction = action;
        if (action.state === 'succeeded' || action.state === 'failed' || action.state === 'expired') {
          operationText = null;
          operationResult = action.state === 'succeeded'
            ? 'Acción confirmada por el dispositivo.'
            : describeFailure(action);
          if (action.state === 'succeeded' && isCameraType(action.type) && action.imageAvailable) {
            await loadCameraImage(action.id, token);
          }
          if (action.state !== 'succeeded') setError(describeFailure(action));
          refreshStatus();
          updateControls();
          void refreshControlAfterTerminal();
          return;
        }
        refreshStatus();
        updateControls();
      } catch (err) {
        if (!alive || token !== actionToken || ctl.signal.aborted) return;
        if (err instanceof RequestAbortError) return;
        setError(classifyError(err));
        return;
      }
      await delay(jitter(POLL_MIN_MS, POLL_MAX_MS), ctl.signal);
      if (ctl.signal.aborted || !alive || token !== actionToken) return;
    }
  }

  async function dispatch(body: DeviceActionRequest, text: string): Promise<void> {
    const safeStop = ['audio.disable', 'wake.disable', 'session.stop'].includes(body.type);
    if (!alive || (isPending() && !safeStop)) return;
    stopPolling();
    const ctl = new AbortController(); pollAbort = ctl;
    const token = ++actionToken;
    sending = true;
    releasePreview();
    setError(null);
    operationResult = null;
    currentAction = null;
    operationText = text;
    refreshStatus();
    updateControls();
    try {
      const { action } = await controls.create(deviceId, body, AbortSignal.any([ctl.signal, lifetime.signal]));
      if (!alive || token !== actionToken) return;
      sending = false;
      currentAction = action;
      refreshStatus();
      updateControls();
      const startedAt = Date.now();
      await pollAction(action.id, startedAt, token);
    } catch (err) {
      if (!alive || token !== actionToken) return;
      sending = false;
      setError(`${classifyError(err)} La acción no se ha confirmado.`);
      operationText = null;
      currentAction = null;
      operationResult = null;
      refreshStatus();
      updateControls();
    }
  }

  audioEnable.addEventListener('click', () => {
    if (audioEnable.disabled) return;
    audioConsent.checked = false;
    void dispatch({ type: 'audio.enable', consent: true }, 'Habilitando audio…');
  });
  audioDisable.addEventListener('click', () => {
    if (audioDisable.disabled) return;
    audioConsent.checked = false;
    void dispatch({ type: 'audio.disable', consent: true }, 'Silenciando ASIS…');
  });
  wakeEnable.addEventListener('click', () => {
    if (wakeEnable.disabled) return;
    wakeConsent.checked = false;
    void dispatch({ type: 'wake.enable', consent: true }, 'Activando Oye ASIS…');
  });
  wakeDisable.addEventListener('click', () => {
    if (wakeDisable.disabled) return;
    wakeConsent.checked = false;
    void dispatch({ type: 'wake.disable', consent: true }, 'Desactivando activación local…');
  });
  sessionStop.addEventListener('click', () => {
    if (sessionStop.disabled) return;
    void dispatch({ type: 'session.stop', consent: true }, 'Parando conversación…');
  });
  cameraCapture.addEventListener('click', () => {
    if (cameraCapture.disabled) return;
    cameraConsent.checked = false;
    void dispatch({ type: 'camera.capture', consent: true }, 'Tomando foto…');
  });
  refreshBtn.addEventListener('click', () => { if (!refreshBtn.disabled) void loadControl(); });

  audioConsent.addEventListener('change', () => { updateControls(); });
  wakeConsent.addEventListener('change', () => { updateControls(); });
  cameraConsent.addEventListener('change', () => { updateControls(); });

  updateControls(); void loadControl();

  return {
    el: root,
    update() { if (alive) void loadControl(); },
    destroy() {
      alive = false;
      lifetime.abort();
      actionToken += 1;
      stopPolling();
      releasePreview();
      audioConsent.checked = false; wakeConsent.checked = false; cameraConsent.checked = false;
    },
  };
}
